import { Link } from '@tanstack/react-router'
import {
  Award,
  Calendar,
  FileText,
  Users,
  ChevronRight,
  Zap,
} from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { useBenefitsJourneyProgress } from '@/features/benefits/hooks/useBenefitsJourneyProgress'

const JOURNEY_SECTIONS = [
  {
    id: 'cpd',
    title: 'CPD Bank',
    description: 'Continuous Professional Development credits and certifications',
    icon: Award,
    url: '/benefits/cpd-bank',
    color: 'from-blue-500 to-blue-600',
  },
  {
    id: 'training',
    title: 'Training & Events',
    description: 'Professional development events, workshops, and webinars',
    icon: Calendar,
    url: '/benefits/calendar',
    color: 'from-purple-500 to-purple-600',
  },
  {
    id: 'documents',
    title: 'Essential Documents',
    description: 'Important resources, guidelines, and reference materials',
    icon: FileText,
    url: '/membership/documents',
    color: 'from-amber-500 to-amber-600',
  },
  {
    id: 'communities',
    title: 'Communities',
    description: 'Join specialized interest groups and communities',
    icon: Users,
    url: '/benefits/communities',
    color: 'from-emerald-500 to-emerald-600',
  },
  // {
  //   id: 'resources',
  //   title: 'Learning Resources',
  //   description: 'Access exclusive books, articles, and learning materials',
  //   icon: BookOpen,
  //   url: '#',
  //   color: 'from-cyan-500 to-cyan-600',
  // },
]

function JourneyCard({
  section,
  progress,
}: {
  section: (typeof JOURNEY_SECTIONS)[0]
  progress: number
}) {
  const Icon = section.icon

  return (
    <Link to={section.url} disabled={section.url === '#'}>
      <Card className="group cursor-pointer transition-all hover:shadow-lg hover:border-primary/50">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className={`rounded-lg bg-gradient-to-br ${section.color} p-3 text-white`}>
                <Icon className="size-5" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-base group-hover:text-primary transition">
                  {section.title}
                </CardTitle>
              </div>
            </div>
            <ChevronRight className="size-5 text-muted-foreground group-hover:translate-x-1 transition-transform" />
          </div>
          <CardDescription className="text-xs">{section.description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-semibold">{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </CardContent>
      </Card>
    </Link>
  )
}

export function BenefitsJourneyPage() {
  const journeyProgress = useBenefitsJourneyProgress()

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <div className="rounded-lg border border-primary/20 bg-gradient-to-r from-primary/5 via-primary/5 to-secondary/5 p-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="rounded-full bg-gradient-to-br from-primary to-secondary p-3">
            <Zap className="size-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Benefits Journey</h1>
        </div>
        <p className="text-lg text-muted-foreground max-w-2xl mb-6">
          Your comprehensive guide to maximizing your UIBFS membership benefits. Explore professional development opportunities, connect with peers, and grow your career with exclusive member resources.
        </p>

        {/* Overall Progress */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-semibold">Your Journey Progress</span>
            <Badge className="bg-primary text-white">{journeyProgress.totalProgress}%</Badge>
          </div>
          <Progress value={journeyProgress.totalProgress} className="h-3" />
          <p className="text-sm text-muted-foreground">
            You're actively engaging with {journeyProgress.sections.filter((s) => s.progress > 0).length} of {journeyProgress.sections.length} benefit categories
          </p>
        </div>
      </div>

      {/* Journey Sections Grid */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight mb-2">Explore Your Benefits</h2>
          <p className="text-muted-foreground">
            Each section contributes to your overall Benefits Score. Start with any area that interests you.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {JOURNEY_SECTIONS.map((section, idx) => {
            const sectionProgress = journeyProgress.sections.find((s) => s.name.toLowerCase().includes(section.id))
            return (
              <JourneyCard
                key={section.id}
                section={section}
                progress={sectionProgress?.progress ?? (idx === 0 ? 85 : idx === 1 ? 60 : idx === 2 ? 50 : idx === 3 ? 80 : 0)}
              />
            )
          })}
        </div>
      </div>

      {/* Detailed Progress Section */}
      <Card>
        <CardHeader>
          <CardTitle>Your Progress Overview</CardTitle>
          <CardDescription>
            See how you're progressing through each category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {journeyProgress.sections.map((section, idx) => (
              <div key={idx} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{section.name}</span>
                  <span className="text-sm text-muted-foreground">{section.description}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Progress value={section.progress} className="h-2 flex-1" />
                  <span className="font-semibold text-sm min-w-fit">{section.progress}%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="text-xl">Ready to advance your journey?</CardTitle>
          <CardDescription>
            Maximize your benefits score by actively participating in all available categories.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm mb-6">
            <li className="flex gap-2 items-start">
              <span className="text-primary font-bold">•</span>
              <span>Complete CPD requirements to earn professional credits</span>
            </li>
            <li className="flex gap-2 items-start">
              <span className="text-primary font-bold">•</span>
              <span>Attend training events to expand your knowledge</span>
            </li>
            <li className="flex gap-2 items-start">
              <span className="text-primary font-bold">•</span>
              <span>Join communities to network with peers</span>
            </li>
            <li className="flex gap-2 items-start">
              <span className="text-primary font-bold">•</span>
              <span>Review essential documents for compliance and growth</span>
            </li>
          </ul>
          <Button asChild className="w-full sm:w-auto">
            <Link to="/benefits/cpd-bank">Start with CPD Bank</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}
